import { Axiom } from "@axiomhq/js";

export interface LogContext {
	contentId?: string;
	userId?: string;
	sessionId?: string;
	traceId?: string;
	[key: string]: unknown;
}

type LogLevel = "info" | "warn" | "error" | "debug";

interface AxiomLoggerConfig {
	token?: string;
	dataset?: string;
	source: string;
	branch: string;
	extraFields?: Record<string, unknown>;
	consoleLog?: boolean;
}

export function createAxiomLogger(config: AxiomLoggerConfig) {
	const {
		token,
		dataset = "logs",
		source,
		branch,
		extraFields,
		consoleLog = false,
	} = config;

	const axiomClient = token ? new Axiom({ token }) : null;

	if (!token) {
		console.warn("Axiom token not configured, logging disabled");
	}

	function log(level: LogLevel, message: string, context: LogContext = {}) {
		const entry = {
			level,
			message,
			timestamp: new Date().toISOString(),
			source,
			branch,
			...extraFields,
			...context,
		};

		if (consoleLog) {
			const consoleMethod =
				level === "error" ? console.error : console.log;
			consoleMethod(
				`[${level}] ${message}`,
				JSON.stringify(context, null, 2),
			);
		}

		if (axiomClient) {
			axiomClient.ingest(dataset, [entry]);
		}
	}

	const axiomLog = {
		info: (message: string, context?: LogContext) =>
			log("info", message, context),
		warn: (message: string, context?: LogContext) =>
			log("warn", message, context),
		error: (message: string, context?: LogContext) =>
			log("error", message, context),
		debug: (message: string, context?: LogContext) =>
			log("debug", message, context),
		flush: async () => {
			await axiomClient?.flush();
		},
	};

	return { axiomLog, axiomClient };
}
